import {hasBlockContent} from '@shm/shared/content'
import {HMBlockNode} from '@seed-hypermedia/client/hm-types'
import {useEffect, useRef, useState} from 'react'

interface CommentDraft {
  blocks: HMBlockNode[]
  mediaRefs?: Record<string, string> // blockId to serialized mediaRef JSON map
  timestamp: number
}

/** Range of a quoted fragment within a block, in codepoint offsets. */
export type QuotingRange = {start: number; end: number}

// Generate a unique key for storing drafts
function getDraftKey(
  docId: string,
  replyCommentId?: string | null,
  quotingBlockId?: string,
  quotingRange?: QuotingRange,
): string {
  const parts = ['comment-draft', docId]
  if (replyCommentId) parts.push(`reply-${replyCommentId}`)
  if (quotingBlockId) parts.push(`quote-${quotingBlockId}`)
  if (quotingRange) parts.push(`range-${quotingRange.start}-${quotingRange.end}`)
  return parts.join('-')
}

// Clean up old drafts (older than 30 days) and empty drafts
function cleanupOldDrafts() {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
  const keysToRemove: string[] = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith('comment-draft-')) {
      try {
        const draft = JSON.parse(localStorage.getItem(key) || '{}') as CommentDraft

        // Remove if older than 30 days
        if (draft.timestamp < thirtyDaysAgo) {
          keysToRemove.push(key)
          continue
        }

        // Remove if content is empty
        if (draft.blocks && !draft.blocks.some(hasBlockContent)) {
          keysToRemove.push(key)
        }
      } catch {
        // Invalid draft, remove it
        keysToRemove.push(key)
      }
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key))
}

export function useCommentDraftPersistence(
  docId: string,
  replyCommentId?: string | null,
  quotingBlockId?: string,
  quotingRange?: QuotingRange,
) {
  const draftKey = getDraftKey(docId, replyCommentId, quotingBlockId, quotingRange)
  const [draft, setDraftState] = useState<CommentDraft | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  // Load draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(draftKey)
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft) as CommentDraft
        setDraftState(parsed)
      } else {
        setDraftState(null)
      }
    } catch (error) {
      console.error('Failed to load comment draft:', error)
    } finally {
      setIsLoading(false)
    }

    // Clean up old drafts periodically
    cleanupOldDrafts()
  }, [draftKey])

  const saveDraft = (blocks: HMBlockNode[], mediaRefs?: Record<string, string>) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Check if content is empty
    const hasContent = blocks.some(hasBlockContent)

    if (!hasContent) {
      // If content is empty and there's an existing draft, remove it
      const existingDraft = localStorage.getItem(draftKey)
      if (existingDraft) {
        localStorage.removeItem(draftKey)
        setDraftState(null)
      }
      return
    }

    // Debounce the save
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const draft: CommentDraft = {
          blocks,
          timestamp: Date.now(),
        }
        // Save mediaRefs in a map because they get stripped by Zod schema
        if (mediaRefs && Object.keys(mediaRefs).length > 0) {
          draft.mediaRefs = mediaRefs
        }
        localStorage.setItem(draftKey, JSON.stringify(draft))
        // Updating state here caused unnecessary rerenders that caused cursor jumping
        // The draft state is only needed for initial load so no need to update it
        // setDraftState(draft)
      } catch (error) {
        console.error('Failed to save comment draft:', error)
      }
    }, 500)
  }

  const removeDraft = () => {
    try {
      localStorage.removeItem(draftKey)
      setDraftState(null)
      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    } catch (error) {
      console.error('Failed to remove comment draft:', error)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  return {
    draft: draft?.blocks || null,
    draftMediaRefs: draft?.mediaRefs || null,
    isLoading,
    saveDraft,
    removeDraft,
  }
}
